import type { OfflineSyncItem } from '@/tauri/offline-recording/offline-recording';
import type { OfflineLocalSessionListItem } from '@heidi/session/contracts';

/**
 * The sync-queue row that speaks for the session currently being viewed, or
 * `undefined` when nothing in the queue does.
 *
 * Two rules, and both are load-bearing:
 *
 * - **Match on either id.** A session is keyed locally until its server session
 *   exists, then carries both, so matching on one id misses the other half of a
 *   session's life.
 * - **A `recording` row matched only by its SERVER id does not count.** Rust
 *   synthesises a `recording` row for any local offline session that has an
 *   `offline_sessions` row but no `sync_tracking` row — created, never stopped,
 *   nothing on disk. `useRecordingButton` stamps the server id onto such a row
 *   when an offline capture starts from an already-open session, and nothing
 *   stops that happening twice on one server session, so two rows can carry the
 *   same server id and a shell holding nothing says nothing about that server
 *   session. Filtering here rather than guarding after the match is what makes
 *   the result independent of queue order: with `[shell, pending_sync]` both
 *   carrying `server-1`, a plain first-match returns the shell and the real
 *   queued row is never seen.
 *
 * Same shape as `hasCapturedOfflineAudio` in `./useOfflineSessionHasCapturedAudio`,
 * which drops `recording` on both ids because it is asking a different question
 * (is there audio on disk). Here a `recording` row matched by its LOCAL id is
 * kept: that is the session's own in-progress capture, and it is exactly what
 * tells a rehydrated detail route its note is not coming yet.
 *
 * Shared rather than duplicated: `useOfflineNoteOverride` (the note body) and
 * `SessionSyncStatusBadge` (the header) both resolve the same session against
 * the same queue, and two copies of this rule can disagree on the same data.
 */
export function findOfflineSyncQueueItem(
  queue: OfflineSyncItem[],
  sessionId: string | null | undefined
): OfflineSyncItem | undefined {
  if (!sessionId) {
    return undefined;
  }

  return queue.find((entry) => {
    if (entry.localSessionId === sessionId) {
      return true;
    }

    if (entry.serverSessionId !== sessionId) {
      return false;
    }

    return entry.status !== 'recording';
  });
}

/**
 * The local consult a capture click on this id belongs to — which consult do I
 * file this take into — or `undefined` when the queue holds none.
 *
 * Ranked, not filtered, because **two rows can carry one server id**: a shell
 * gets the server id stamped on it when an offline capture starts from an
 * already-open session, and nothing stops that twice, while a pause that
 * outlived a quit is salvaged at the next boot into a real `pending_sync` row.
 * A first-match scan across that pair answers by queue order. Ranking makes the
 * answer depend on what the row is, so rank 3 can accept a shell that rank 2
 * would rather not have.
 *
 * Deliberately NOT {@link findOfflineSyncQueueItem}, which asks what the queue
 * says ABOUT a session and so drops a shell matched by server id. Here
 * `undefined` sends the caller to `create()` and mints a second consult for an
 * encounter the device already holds, so a lone shell must still bind — it is a
 * consult created and never stopped, and its first take belongs in it.
 *
 * Ranks 2 and 3 do not distinguish between two queued takes: both are the same
 * encounter, the caller only needs one that holds audio, and ordering takes is
 * Rust's job at sync.
 */
export function findOfflineCaptureConsult(
  queue: OfflineSyncItem[],
  sessionId: string | null | undefined
): OfflineSyncItem | undefined {
  if (!sessionId) {
    return undefined;
  }

  return (
    // 1. Its own local id — names this consult and no other, so nothing outranks it.
    queue.find((entry) => entry.localSessionId === sessionId) ??
    // 2. Its server id, holding a take: any status but `recording` means `stop()`
    //    completed and audio was encrypted and queued under it.
    queue.find(
      (entry) => entry.serverSessionId === sessionId && entry.status !== 'recording'
    ) ??
    // 3. Its server id, holding nothing — the synthesised `recording` shell.
    queue.find((entry) => entry.serverSessionId === sessionId)
  );
}

/**
 * `recording` is synthesised for any session with no sync row, so the active
 * capture id, which stays set while paused, is what separates a live capture
 * from an empty session.
 */
export function isEmptyOfflineSession(
  item: OfflineSyncItem | undefined,
  activeRecordingId: string | null
): boolean {
  return item?.status === 'recording' && item.localSessionId !== activeRecordingId;
}

/**
 * A local id with no server session in the queue or the local-session list.
 * Both are read because the queue row can lag the list on the server id.
 */
export function isLocalOnlyOfflineSession(
  queue: OfflineSyncItem[],
  localSessions: OfflineLocalSessionListItem[],
  sessionId: string | null | undefined
): boolean {
  if (!sessionId) {
    return false;
  }
  const queued = queue.find((entry) => entry.localSessionId === sessionId);
  const listed = localSessions.find((entry) => entry.localSessionId === sessionId);
  if (!queued && !listed) {
    return false;
  }
  return !queued?.serverSessionId && !listed?.serverSessionId;
}
